<template>
  <div class="wz-backdrop" @click.self="close" @keydown.esc="close">
    <section class="wz designer" role="dialog" aria-modal="true" aria-labelledby="wz-title">
      <header class="wz-head">
        <div><span class="vn-kicker"><b>NEW</b> {{ isVisit ? 'نوع ویزیت تازه' : 'پرسشنامه‌ی تازه' }}</span><h2 id="wz-title">{{ steps[step].title }}</h2></div>
        <button type="button" class="wz-x" aria-label="بستن" @click="close"><VnIcon name="x" /></button>
      </header>
      <ol class="wz-steps" aria-label="مراحل">
        <li v-for="(item, index) in steps" :key="item.key" :class="{ done: index < step, current: index === step }"><span class="vn-num">{{ fa(index + 1) }}</span>{{ item.label }}</li>
      </ol>

      <div class="wz-body">
        <!-- 1. start point -->
        <div v-if="step === 0" class="wz-starts" role="radiogroup" aria-label="نقطه‌ی شروع">
          <button v-for="option in starts" :key="option.key" type="button" role="radio" class="wz-start" :aria-checked="start === option.key ? 'true' : 'false'"
                  :class="{ active: start === option.key }" @click="start = option.key">
            <span class="wz-start-icon"><VnIcon :name="option.icon" /></span>
            <b>{{ option.title }}</b><small>{{ option.text }}</small>
          </button>
          <label v-if="start === 'copy'" class="ins-field wz-wide"><span>فرم مبدأ</span>
            <select v-model="copyFrom"><option :value="null" disabled>یک فرم را انتخاب کنید</option>
              <option v-for="item in sources" :key="item.id" :value="item.id">{{ item.verbose_name }} · {{ fa(item.questions) }} پرسش{{ item.is_active ? '' : ' · غیرفعال' }}</option></select>
          </label>
        </div>

        <!-- 2. details -->
        <div v-else-if="step === 1" class="ins-pane">
          <label class="ins-field"><span>{{ isVisit ? 'نام نوع ویزیت' : 'نام پرسشنامه' }}</span>
            <input ref="name" v-model="details.verbose_name" maxlength="40" :placeholder="isVisit ? 'مثلاً: ویزیت چیدمان فروشگاه‌های زنجیره‌ای' : 'مثلاً: رضایت مشتریان فروشگاه'" />
          </label>
          <label class="ins-field"><span>نام لاتین (کد) <small>اختیاری</small></span><input v-model="details.title" maxlength="40" dir="ltr" :placeholder="isVisit ? 'Merchandising Audit' : 'Customer Satisfaction'" /></label>
          <template v-if="isVisit">
            <label class="ins-field"><span>توضیح <small>اختیاری</small></span><textarea v-model="details.description" rows="2" /></label>
            <label class="ins-field"><span>دسته‌ی فروشگاه</span>
              <select v-model="details.outlet_category"><option :value="null" disabled>انتخاب کنید</option>
                <option v-for="item in categories" :key="item.id" :value="item.id">{{ item.verbose_name || item.name }}</option></select>
            </label>
            <label class="ins-toggle"><input v-model="details.has_supervision" type="checkbox" /><span><b>سرکشی دارد</b><small>سرپرست‌ها مسیر جداگانه‌ای از پرسش و تصویر دارند.</small></span></label>
          </template>
          <AIFormAssistant v-if="start === 'ai'" :kind="kind" :category="details.outlet_category" :answer-types="answerTypes" :initial-draft="aiDraft" @draft="adoptDraft" @busy="aiBusy = $event" />
        </div>

        <!-- 3. review -->
        <div v-else class="wz-review">
          <dl>
            <div><dt>نام</dt><dd>{{ details.verbose_name }}</dd></div>
            <div v-if="details.title"><dt>کد</dt><dd dir="ltr">{{ details.title }}</dd></div>
            <div v-if="isVisit"><dt>دسته‌ی فروشگاه</dt><dd>{{ categoryName }}</dd></div>
            <div v-if="isVisit"><dt>سرکشی</dt><dd>{{ details.has_supervision ? 'دارد' : 'ندارد' }}</dd></div>
            <div><dt>شروع از</dt><dd>{{ startText }}</dd></div>
          </dl>
          <p v-if="aiDraft" class="ins-note">{{ aiDraft.summary }} · {{ fa(aiQuestions) }} پرسش. فرم به‌صورت غیرفعال ساخته می‌شود؛ پس از بازبینی در طراح می‌توانید آن را فعال کنید.</p>
          <p class="ins-note">پس از ساخت، طراح فرم باز می‌شود تا بخش‌ها، پرسش‌ها، قوانین و مراحل تصویر را بچینید.
            {{ start === 'ai' ? 'پیش‌نویس تا وقتی فعالش نکنید در اپ میدانی نمایش داده نمی‌شود.' : isVisit ? 'تا وقتی ویزیتی از این نوع برنامه‌ریزی نشود، کارشناسان آن را نمی‌بینند.' : 'پرسشنامه‌ی فعال بلافاصله در فهرست پرسشنامه‌های اپ دیده می‌شود؛ تا پایان طراحی می‌توانید آن را غیرفعال کنید.' }}</p>
        </div>
        <p v-if="error" class="ins-error" role="alert">{{ error }}</p>
      </div>

      <footer class="wz-foot">
        <button v-if="step > 0" type="button" class="ins-btn" :disabled="busy || aiBusy" @click="step -= 1">مرحله‌ی قبل</button>
        <span class="wz-gap" />
        <button v-if="step < steps.length - 1" type="button" class="wz-next" :disabled="!canNext" @click="next">ادامه</button>
        <button v-else type="button" class="wz-next" :disabled="busy || aiBusy" @click="create">{{ busy ? 'در حال ساخت…' : start === 'ai' ? 'ساخت پیش‌نویس و ورود به طراح' : 'ساخت و ورود به طراح' }}</button>
      </footer>
    </section>
  </div>
</template>

<script>
import VnIcon from '@/components/VnIcon/index.vue';
import { fa } from './designerModel';
import './designer.css';
import AIFormAssistant from './components/AIFormAssistant.vue';

export default {
  name: 'NewFormWizard',
  components: { VnIcon, AIFormAssistant },
  props: {
    kind: { type: String, required: true },
    sources: { type: Array, default: () => [] },
    categories: { type: Array, default: () => [] },
    preset: { type: Object, default: null }, // { copyFrom }
  },
  data() {
    return {
      step: 0, start: this.preset && this.preset.copyFrom ? 'copy' : (this.kind === 'visit' ? 'starter' : 'blank'), copyFrom: this.preset ? this.preset.copyFrom : null,
      details: { verbose_name: '', title: '', description: '', outlet_category: null, has_supervision: false }, busy: false, error: '',
      aiAvailable: false, aiBusy: false, aiDraft: null, answerTypes: [],
    };
  },
  computed: {
    isVisit() { return this.kind === 'visit'; },
    steps() { return [{ key: 'start', label: 'نقطه‌ی شروع', title: 'از کجا شروع کنیم؟' }, { key: 'details', label: 'مشخصات', title: 'مشخصات فرم' }, { key: 'review', label: 'مرور', title: 'آماده‌ی ساخت' }]; },
    starts() {
      return [
        ...(this.aiAvailable ? [{ key: 'ai', icon: 'wand', title: 'با دستیار هوش مصنوعی', text: 'توضیح بدهید؛ مراحل، پرسش‌ها و تصاویر را به‌صورت پیش‌نویس تحویل بگیرید.' }] : []),
        ...(this.isVisit ? [{ key: 'starter', icon: 'wand', title: 'الگوی پایه', text: 'تصویر ورود، بخش سوالات عمومی و تصویر خروج؛ آماده‌ی تکمیل.' }] : []),
        { key: 'blank', icon: 'plus', title: 'فرم خالی', text: 'همه‌چیز را از صفر بچینید.' },
        { key: 'copy', icon: 'duplicate', title: 'رونوشت از فرم موجود', text: 'بخش‌ها، پرسش‌ها، قوانین و تصاویر یک فرم دیگر کپی می‌شوند.' },
      ];
    },
    canNext() {
      if (this.busy || this.aiBusy) return false;
      if (this.step === 0) return this.start !== 'copy' || !!this.copyFrom;
      return !!this.details.verbose_name.trim() && (!this.isVisit || !!this.details.outlet_category) && (this.start !== 'ai' || !!this.aiDraft);
    },
    aiQuestions() { return this.aiDraft ? Object.values(this.aiDraft.tree.tracks).reduce((n, b) => n + b.sections.reduce((sum, s) => sum + s.questions.length, 0), 0) : 0; },
    categoryName() { const row = this.categories.find(item => item.id === this.details.outlet_category); return row ? (row.verbose_name || row.name) : '—'; },
    startText() {
      if (this.start === 'ai') return 'پیشنهاد دستیار هوش مصنوعی';
      if (this.start === 'copy') { const row = this.sources.find(item => item.id === this.copyFrom); return `رونوشت «${row ? row.verbose_name : ''}»`; }
      return this.start === 'starter' ? 'الگوی پایه' : 'فرم خالی';
    },
  },
  watch: {
    copyFrom(id) {
      const row = this.sources.find(item => item.id === id);
      if (row && !this.details.verbose_name) this.details.verbose_name = `${row.verbose_name} (رونوشت)`.slice(0, 40);
    },
  },
  mounted() {
    if (this.categories.length === 1) this.details.outlet_category = this.categories[0].id;
    this.onKey = (event) => { if (event.key === 'Escape') this.close(); };
    window.addEventListener('keydown', this.onKey);
    const project = this.$STORE.state.userConfig.setProjectId;
    Promise.all([this.$ApiServiceLayer.get(`/core/api/ai/capabilities/?p=${project}`), this.$ApiServiceLayer.get(`/core/api/admin/designer/catalog/?p=${project}`)]).then(([ai, catalog]) => { this.aiAvailable = ai.status === 200 && ai.data.enabled; if (catalog.status === 200) this.answerTypes = catalog.data.answer_types; });
  },
  beforeDestroy() { window.removeEventListener('keydown', this.onKey); },
  methods: {
    fa,
    close() { if (!this.busy && !this.aiBusy) this.$emit('close'); },
    adoptDraft(draft) { this.aiDraft = draft; if (!draft) return; if (!this.details.verbose_name) this.details.verbose_name = draft.tree.form.verbose_name; if (!this.details.title) this.details.title = draft.tree.form.title; if (!this.details.description) this.details.description = draft.tree.form.description; this.details.has_supervision = draft.tree.form.has_supervision; },
    next() {
      if (!this.canNext) return;
      this.step += 1;
      if (this.step === 1) this.$nextTick(() => this.$refs.name && this.$refs.name.focus());
    },
    async create() {
      this.busy = true; this.error = '';
      const project = this.$STORE.state.userConfig.setProjectId;
      const body = { ...this.details, starter: this.start === 'starter', copy_from: this.start === 'copy' ? this.copyFrom : null };
      if (this.start === 'ai' && this.aiDraft) body.ai_proposal = this.aiDraft.proposal;
      const res = await this.$ApiServiceLayer.post(`/core/api/admin/designer/${this.isVisit ? 'visit_types' : 'surveys'}/?p=${project}`, '', body);
      this.busy = false;
      if (res.status === 201) { this.$emit('created', res.data.form.id); return; }
      this.error = (res.data && res.data.detail) || this.$ApiServiceLayer.getErrorMessage(res);
    },
  },
};
</script>

<style scoped>
.wz-backdrop { position: fixed; inset: 0; z-index: 1050; display: grid; place-items: center; padding: 16px; background: rgba(2, 11, 45, .55); }
.wz { width: min(640px, 100%); max-height: calc(100vh - 32px); display: flex; flex-direction: column; border-radius: var(--r-md); background: var(--surface); box-shadow: var(--shadow-pop); overflow: hidden; }
.wz-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; padding: 18px 20px 6px; }
.wz-head h2 { margin: 4px 0 0; font-size: 18px; font-weight: 900; color: var(--navy-900); }
.wz-x { width: 34px; height: 34px; display: grid; place-items: center; border: 1px solid var(--line); border-radius: var(--r-xs); background: transparent; cursor: pointer; }
.wz-steps { display: flex; gap: 6px; margin: 8px 20px 0; padding: 0; list-style: none; }
.wz-steps li { flex: 1; display: flex; align-items: center; gap: 8px; padding: 8px 0; border-top: 3px solid var(--line); font-size: 12px; font-weight: 800; color: var(--text-4); }
.wz-steps li span { width: 22px; height: 22px; display: grid; place-items: center; border-radius: var(--r-xs); background: var(--navy-50); font-size: 11px; }
.wz-steps li.done { border-color: var(--teal-500); color: var(--text-2); }
.wz-steps li.done span { background: var(--teal-500); color: var(--navy-900); }
.wz-steps li.current { border-color: var(--navy-900); color: var(--navy-900); }
.wz-steps li.current span { background: var(--navy-900); color: var(--amber-500); }
.wz-body { flex: 1; overflow-y: auto; padding: 16px 20px; display: grid; gap: 12px; align-content: start; }
.wz-starts { display: grid; grid-template-columns: repeat(auto-fit, minmax(170px, 1fr)); gap: 10px; }
.wz-start { display: grid; gap: 6px; justify-items: start; padding: 14px; border: 1px solid var(--line-strong); border-radius: var(--r-sm); background: var(--surface); text-align: right; font: inherit; cursor: pointer; }
.wz-start b { font-size: 13.5px; color: var(--text); }
.wz-start small { font-size: 11.5px; color: var(--text-3); line-height: 1.8; }
.wz-start-icon { width: 36px; height: 36px; display: grid; place-items: center; border-radius: var(--r-xs); background: var(--navy-50); color: var(--navy-900); --icon-accent: var(--teal-500); }
.wz-start:hover { border-color: var(--navy-900); }
.wz-start.active { border-color: var(--navy-900); box-shadow: 4px 4px 0 var(--teal-500); }
.wz-start.active .wz-start-icon { background: var(--navy-900); color: #fff; --icon-accent: var(--amber-500); }
.wz-wide { grid-column: 1 / -1; }
.wz-review dl { display: grid; gap: 0; margin: 0; border: 1px solid var(--line); border-radius: var(--r-sm); overflow: hidden; }
.wz-review dl > div { display: grid; grid-template-columns: 140px 1fr; gap: 10px; padding: 10px 14px; font-size: 13px; }
.wz-review dl > div + div { border-top: 1px solid var(--line); }
.wz-review dt { color: var(--text-3); font-weight: 700; }
.wz-review dd { margin: 0; font-weight: 800; color: var(--text); }
.wz-foot { display: flex; align-items: center; gap: 10px; padding: 14px 20px; border-top: 1px solid var(--line); background: var(--surface-sunken); }
.wz-gap { flex: 1; }
.wz-next { min-height: 40px; padding: 0 20px; border: 2px solid var(--navy-900); border-radius: var(--r-xs); background: var(--navy-900); color: #fff; font: inherit; font-size: 13px; font-weight: 900; cursor: pointer; box-shadow: 3px 3px 0 var(--teal-500); }
.wz-next:disabled { opacity: .45; cursor: default; box-shadow: none; }
</style>
